import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TestTasksTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function TestTasksSubTab() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [topics, setTopics] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [filters, setFilters] = useState({
    company_id: '',
    topic_id: '',
    task_type_id: ''
  });
  const [formData, setFormData] = useState({
    task_type_id: '',
    question_text: '',
    description: '',
    correct_answer: '',
    company_id: '',
    topic_id: ''
  });

  // Специальный state для step-by-step типа
  const [stepByStepData, setStepByStepData] = useState({
    correctSteps: [''],
    falseOptions: ['']
  });

  // Специальный state для типа correct-answer (варианты ответов + индексы правильных)
  const [correctAnswerData, setCorrectAnswerData] = useState({
    answerOptions: ['', ''],
    correctAnswers: []
  });

  useEffect(() => {
    fetchTasks();
    fetchCompanies();
    fetchTopics();
    fetchTaskTypes();
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [filters]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.company_id) params.company_id = filters.company_id;
      if (filters.topic_id) params.topic_id = filters.topic_id;
      if (filters.task_type_id) params.task_type_id = filters.task_type_id;
      
      const response = await axios.get(`${API_URL}/api/test-tasks`, { params });
      setTasks(response.data);
      setError(null);
    } catch (err) {
      let errorMsg = 'Ошибка при загрузке заданий';
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMsg = err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail)) {
          errorMsg = err.response.data.detail.map(e => e.msg || JSON.stringify(e)).join('; ');
        } else {
          errorMsg = JSON.stringify(err.response.data.detail);
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/companies`);
      setCompanies(response.data);
    } catch (err) {
      console.error('Ошибка при загрузке компаний:', err);
    }
  };

  const fetchTopics = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/topics`);
      setTopics(response.data);
    } catch (err) {
      console.error('Ошибка при загрузке технологий:', err);
    }
  };

  const fetchTaskTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/test-task-types`);
      setTaskTypes(response.data);
    } catch (err) {
      console.error('Ошибка при загрузке типов заданий:', err);
    }
  };

  const isStepByStepType = () => {
    const selectedType = taskTypes.find(t => t.id === parseInt(formData.task_type_id));
    return selectedType && selectedType.name.toLowerCase().includes('step-by-step');
  };

  const isWhatIsOutputType = () => {
    const selectedType = taskTypes.find(t => t.id === parseInt(formData.task_type_id));
    return selectedType && selectedType.name.toLowerCase().includes('what-is-output');
  };

  const isCorrectAnswerType = () => {
    const selectedType = taskTypes.find(t => t.id === parseInt(formData.task_type_id));
    return selectedType && selectedType.name.toLowerCase().includes('correct-answer');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let correctAnswerValue;

      // Если тип step-by-step, формируем JSON из массивов
      if (isStepByStepType()) {
        const filteredCorrectSteps = stepByStepData.correctSteps.filter(s => s.trim() !== '');
        const filteredFalseOptions = stepByStepData.falseOptions.filter(s => s.trim() !== '');
        
        if (filteredCorrectSteps.length === 0) {
          setError('Добавьте хотя бы один правильный шаг');
          return;
        }

        correctAnswerValue = JSON.stringify({
          correct_steps: filteredCorrectSteps,
          false_options: filteredFalseOptions
        });
      } else if (isCorrectAnswerType()) {
        const validOptions = correctAnswerData.answerOptions.filter(o => o.trim() !== '');
        if (validOptions.length < 2) {
          setError('Добавьте минимум 2 варианта ответа');
          return;
        }
        if (correctAnswerData.correctAnswers.length === 0) {
          setError('Отметьте хотя бы один правильный ответ (кружок рядом с вариантом)');
          return;
        }
        // Индексы правильных ответов относительно отфильтрованного списка (только непустые варианты)
        const indexMap = [];
        correctAnswerData.answerOptions.forEach((opt, idx) => {
          if (opt.trim() !== '') indexMap.push(idx);
        });
        const correctIndices = correctAnswerData.correctAnswers
          .filter(i => indexMap.includes(i))
          .map(oldIdx => indexMap.indexOf(oldIdx));
        if (correctIndices.length === 0) {
          setError('Выбранные правильные ответы не найдены среди вариантов');
          return;
        }
        correctAnswerValue = JSON.stringify({
          answer_options: validOptions,
          correct_answers: correctIndices
        });
      } else if (isWhatIsOutputType()) {
        // Для типа what-is-output сохраняем как простую строку
        correctAnswerValue = formData.correct_answer.trim();
      } else {
        // Для остальных типов используем текстовое поле
        correctAnswerValue = formData.correct_answer.trim();
      }

      const submitData = {
        task_type_id: parseInt(formData.task_type_id),
        question_text: formData.question_text,
        description: formData.description.trim() || null,
        correct_answer: correctAnswerValue,
        company_id: parseInt(formData.company_id),
        topic_id: parseInt(formData.topic_id)
      };

      if (editingTask) {
        await axios.put(`${API_URL}/api/test-tasks/${editingTask.id}`, submitData);
      } else {
        await axios.post(`${API_URL}/api/test-tasks`, submitData);
      }
      setShowForm(false);
      setEditingTask(null);
      setFormData({
        task_type_id: '',
        question_text: '',
        description: '',
        correct_answer: '',
        company_id: '',
        topic_id: ''
      });
      setStepByStepData({
        correctSteps: [''],
        falseOptions: ['']
      });
      setCorrectAnswerData({
        answerOptions: ['', ''],
        correctAnswers: []
      });
      fetchTasks();
    } catch (err) {
      let errorMessage = 'Ошибка при сохранении задания';
      if (err.response) {
        if (err.response.data) {
          if (typeof err.response.data.detail === 'string') {
            errorMessage = err.response.data.detail;
          } else if (Array.isArray(err.response.data.detail)) {
            errorMessage = err.response.data.detail.map(e => {
              if (typeof e === 'string') return e;
              if (e.msg) return e.msg;
              if (e.loc && e.msg) return `${e.loc.join('.')}: ${e.msg}`;
              return JSON.stringify(e);
            }).join('; ');
          } else if (err.response.data.detail && typeof err.response.data.detail === 'object') {
            if (err.response.data.detail.message) {
              errorMessage = err.response.data.detail.message;
            } else {
              errorMessage = 'Ошибка валидации данных. Проверьте правильность заполнения полей.';
            }
          } else if (err.response.data.message) {
            errorMessage = err.response.data.message;
          }
        } else {
          errorMessage = `Ошибка сервера: ${err.response.status} ${err.response.statusText}`;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      console.error('Ошибка при сохранении задания:', err);
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    
    // Проверяем тип задания
    const taskType = taskTypes.find(t => t.id === task.task_type_id);
    const isStepByStep = taskType && taskType.name.toLowerCase().includes('step-by-step');
    const isWhatIsOutput = taskType && taskType.name.toLowerCase().includes('what-is-output');
    const isCorrectAnswer = taskType && taskType.name.toLowerCase().includes('correct-answer');
    
    let correctAnswerStr = '';
    
    if (isCorrectAnswer && task.correct_answer) {
      try {
        let obj = task.correct_answer;
        if (typeof obj === 'string') obj = JSON.parse(obj);
        if (obj && (obj.answer_options || obj.correct_answers)) {
          const opts = Array.isArray(obj.answer_options) ? obj.answer_options : [];
          const correct = Array.isArray(obj.correct_answers) ? obj.correct_answers : [];
          setCorrectAnswerData({
            answerOptions: opts.length >= 2 ? opts : ['', ''],
            correctAnswers: correct.filter(i => i >= 0 && i < opts.length)
          });
        } else {
          setCorrectAnswerData({ answerOptions: ['', ''], correctAnswers: [] });
        }
      } catch (e) {
        console.error('Ошибка парсинга correct-answer данных:', e);
        setCorrectAnswerData({ answerOptions: ['', ''], correctAnswers: [] });
      }
    } else {
      setCorrectAnswerData({ answerOptions: ['', ''], correctAnswers: [] });
    }
    
    if (isStepByStep && task.correct_answer) {
      // Для step-by-step парсим JSON
      try {
        let answerObj = task.correct_answer;
        if (typeof answerObj === 'string') {
          answerObj = JSON.parse(answerObj);
        }
        
        if (answerObj.correct_steps || answerObj.false_options) {
          setStepByStepData({
            correctSteps: answerObj.correct_steps || [''],
            falseOptions: answerObj.false_options || ['']
          });
        }
      } catch (e) {
        console.error('Ошибка парсинга step-by-step данных:', e);
        setStepByStepData({
          correctSteps: [''],
          falseOptions: ['']
        });
      }
    } else if (isWhatIsOutput && task.correct_answer) {
      // Для what-is-output извлекаем значение из JSON формата {"value": <значение>} или используем как строку
      try {
        let answerObj = task.correct_answer;
        if (typeof answerObj === 'string') {
          // Пытаемся распарсить как JSON
          try {
            answerObj = JSON.parse(answerObj);
            // Если это объект с полем "value", извлекаем значение
            if (answerObj && typeof answerObj === 'object' && 'value' in answerObj) {
              correctAnswerStr = String(answerObj.value);
            } else {
              // Если не объект с "value", используем как есть
              correctAnswerStr = task.correct_answer;
            }
          } catch (e) {
            // Если не JSON, используем как строку
            correctAnswerStr = task.correct_answer;
          }
        } else if (typeof answerObj === 'object' && answerObj !== null) {
          // Если это объект с полем "value", извлекаем значение
          if ('value' in answerObj) {
            correctAnswerStr = String(answerObj.value);
          } else {
            correctAnswerStr = JSON.stringify(answerObj, null, 2);
          }
        } else {
          correctAnswerStr = String(answerObj);
        }
      } catch (e) {
        console.error('Ошибка обработки what-is-output данных:', e);
        correctAnswerStr = typeof task.correct_answer === 'string' ? task.correct_answer : String(task.correct_answer);
      }
    } else {
      // Для остальных типов
      if (task.correct_answer) {
        if (typeof task.correct_answer === 'object' && task.correct_answer !== null) {
          correctAnswerStr = JSON.stringify(task.correct_answer, null, 2);
        } else if (typeof task.correct_answer === 'string') {
          correctAnswerStr = task.correct_answer;
        } else {
          correctAnswerStr = String(task.correct_answer);
        }
      }
    }
    
    setFormData({
      task_type_id: task.task_type_id.toString(),
      question_text: task.question_text,
      description: task.description || '',
      correct_answer: correctAnswerStr,
      company_id: task.company_id.toString(),
      topic_id: task.topic_id.toString()
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить это задание?')) {
      try {
        await axios.delete(`${API_URL}/api/test-tasks/${id}`);
        fetchTasks();
      } catch (err) {
        setError('Ошибка при удалении задания: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingTask(null);
    setFormData({
      task_type_id: '',
      question_text: '',
      description: '',
      correct_answer: '',
      company_id: '',
      topic_id: ''
    });
    setStepByStepData({
      correctSteps: [''],
      falseOptions: ['']
    });
    setCorrectAnswerData({
      answerOptions: ['', ''],
      correctAnswers: []
    });
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
  };

  // Функции для step-by-step UI
  const addCorrectStep = () => {
    setStepByStepData({
      ...stepByStepData,
      correctSteps: [...stepByStepData.correctSteps, '']
    });
  };

  const removeCorrectStep = (index) => {
    if (stepByStepData.correctSteps.length > 1) {
      const newSteps = stepByStepData.correctSteps.filter((_, i) => i !== index);
      setStepByStepData({ ...stepByStepData, correctSteps: newSteps });
    }
  };

  const updateCorrectStep = (index, value) => {
    const newSteps = [...stepByStepData.correctSteps];
    newSteps[index] = value;
    setStepByStepData({ ...stepByStepData, correctSteps: newSteps });
  };

  const addFalseOption = () => {
    setStepByStepData({
      ...stepByStepData,
      falseOptions: [...stepByStepData.falseOptions, '']
    });
  };

  const removeFalseOption = (index) => {
    if (stepByStepData.falseOptions.length > 1) {
      const newOptions = stepByStepData.falseOptions.filter((_, i) => i !== index);
      setStepByStepData({ ...stepByStepData, falseOptions: newOptions });
    }
  };

  const updateFalseOption = (index, value) => {
    const newOptions = [...stepByStepData.falseOptions];
    newOptions[index] = value;
    setStepByStepData({ ...stepByStepData, falseOptions: newOptions });
  };

  const moveStepUp = (index) => {
    if (index > 0) {
      const newSteps = [...stepByStepData.correctSteps];
      [newSteps[index - 1], newSteps[index]] = [newSteps[index], newSteps[index - 1]];
      setStepByStepData({ ...stepByStepData, correctSteps: newSteps });
    }
  };

  const moveStepDown = (index) => {
    if (index < stepByStepData.correctSteps.length - 1) {
      const newSteps = [...stepByStepData.correctSteps];
      [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];
      setStepByStepData({ ...stepByStepData, correctSteps: newSteps });
    }
  };

  // Функции для correct-answer UI
  const addCorrectAnswerOption = () => {
    setCorrectAnswerData({
      ...correctAnswerData,
      answerOptions: [...correctAnswerData.answerOptions, '']
    });
  };

  const removeCorrectAnswerOption = (index) => {
    const newOptions = correctAnswerData.answerOptions.filter((_, i) => i !== index);
    const newCorrect = correctAnswerData.correctAnswers
      .filter(idx => idx !== index)
      .map(idx => (idx > index ? idx - 1 : idx));
    setCorrectAnswerData({
      answerOptions: newOptions.length >= 2 ? newOptions : ['', ''],
      correctAnswers: newCorrect
    });
  };

  const updateCorrectAnswerOption = (index, value) => {
    const newOptions = [...correctAnswerData.answerOptions];
    newOptions[index] = value;
    setCorrectAnswerData({ ...correctAnswerData, answerOptions: newOptions });
  };

  const toggleCorrectAnswerOption = (index) => {
    const has = correctAnswerData.correctAnswers.includes(index);
    const newCorrect = has
      ? correctAnswerData.correctAnswers.filter(i => i !== index)
      : [...correctAnswerData.correctAnswers, index].sort((a, b) => a - b);
    setCorrectAnswerData({ ...correctAnswerData, correctAnswers: newCorrect });
  };

  if (loading && tasks.length === 0) {
    return <div className="loading">Загрузка...</div>;
  }

  return (
    <div className="test-tasks-subtab">
      <div className="tab-header">
        <h3>Управление тестовыми заданиями</h3>
        {!showForm && (
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + Добавить задание
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {!showForm && (
        <div className="filters-container">
          <div className="filter-group">
            <label>Компания:</label>
            <select
              value={filters.company_id}
              onChange={(e) => handleFilterChange('company_id', e.target.value)}
            >
              <option value="">Все компании</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Технология:</label>
            <select
              value={filters.topic_id}
              onChange={(e) => handleFilterChange('topic_id', e.target.value)}
            >
              <option value="">Все технологии</option>
              {topics.map((topic) => (
                <option key={topic.id} value={topic.id}>
                  {topic.title}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Тип задания:</label>
            <select
              value={filters.task_type_id}
              onChange={(e) => handleFilterChange('task_type_id', e.target.value)}
            >
              <option value="">Все типы</option>
              {taskTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {showForm && (
        <div className="form-container">
          <h4>{editingTask ? 'Редактировать задание' : 'Добавить новое задание'}</h4>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="task_type_id">Тип задания *</label>
              <select
                id="task_type_id"
                value={formData.task_type_id}
                onChange={(e) => setFormData({ ...formData, task_type_id: e.target.value })}
                required
              >
                <option value="">Выберите тип задания</option>
                {taskTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="company_id">Компания *</label>
              <select
                id="company_id"
                value={formData.company_id}
                onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                required
              >
                <option value="">Выберите компанию</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="topic_id">Технология *</label>
              <select
                id="topic_id"
                value={formData.topic_id}
                onChange={(e) => setFormData({ ...formData, topic_id: e.target.value })}
                required
              >
                <option value="">Выберите технологию</option>
                {topics.map((topic) => (
                  <option key={topic.id} value={topic.id}>
                    {topic.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="question_text">Текст вопроса *</label>
              <textarea
                id="question_text"
                value={formData.question_text}
                onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                required
                rows={5}
                placeholder="Введите текст вопроса"
              />
            </div>
            <div className="form-group">
              <label htmlFor="description">Описание вопроса (необязательно)</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                placeholder="Введите дополнительное описание или пояснение к вопросу"
              />
              <small style={{color: '#666', fontSize: '0.9em', display: 'block', marginTop: '5px'}}>
                Описание будет отображаться под вопросом в боте и miniapp
              </small>
            </div>
            
            {/* Условный рендеринг в зависимости от типа задания */}
            {isStepByStepType() ? (
              <div className="step-by-step-editor">
                <div className="step-by-step-section">
                  <label>Правильные шаги (в правильном порядке) *</label>
                  <small style={{color: '#666', display: 'block', marginBottom: '10px'}}>
                    Укажите шаги в правильной последовательности. Пользователь должен будет выбрать их в этом порядке.
                  </small>
                  {stepByStepData.correctSteps.map((step, index) => (
                    <div key={index} className="step-item">
                      <span className="step-number">{index + 1}.</span>
                      <input
                        type="text"
                        value={step}
                        onChange={(e) => updateCorrectStep(index, e.target.value)}
                        placeholder={`Шаг ${index + 1}`}
                        className="step-input"
                      />
                      <div className="step-actions">
                        <button
                          type="button"
                          onClick={() => moveStepUp(index)}
                          disabled={index === 0}
                          className="btn-step-move"
                          title="Переместить вверх"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveStepDown(index)}
                          disabled={index === stepByStepData.correctSteps.length - 1}
                          className="btn-step-move"
                          title="Переместить вниз"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          onClick={() => removeCorrectStep(index)}
                          disabled={stepByStepData.correctSteps.length === 1}
                          className="btn-step-remove"
                          title="Удалить шаг"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addCorrectStep}
                    className="btn-add-step"
                  >
                    + Добавить шаг
                  </button>
                </div>

                <div className="step-by-step-section">
                  <label>Ложные варианты (необязательно)</label>
                  <small style={{color: '#666', display: 'block', marginBottom: '10px'}}>
                    Добавьте неправильные варианты ответов, которые будут показаны вместе с правильными шагами в случайном порядке.
                  </small>
                  {stepByStepData.falseOptions.map((option, index) => (
                    <div key={index} className="step-item">
                      <span className="step-number false">✗</span>
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => updateFalseOption(index, e.target.value)}
                        placeholder={`Ложный вариант ${index + 1}`}
                        className="step-input"
                      />
                      <button
                        type="button"
                        onClick={() => removeFalseOption(index)}
                        disabled={stepByStepData.falseOptions.length === 1}
                        className="btn-step-remove"
                        title="Удалить вариант"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addFalseOption}
                    className="btn-add-step"
                  >
                    + Добавить ложный вариант
                  </button>
                </div>
              </div>
            ) : isCorrectAnswerType() ? (
              <div className="form-group correct-answer-editor">
                <label>Варианты ответов *</label>
                <small className="form-hint" style={{ display: 'block', marginBottom: '10px' }}>
                  Минимум 2 варианта. Отметьте правильные ответы, нажав на кружок рядом с вариантом.
                </small>
                {correctAnswerData.answerOptions.map((option, index) => (
                  <div key={index} className="answer-option-row">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => updateCorrectAnswerOption(index, e.target.value)}
                      placeholder={`Вариант ${index + 1}`}
                      className="answer-option-input"
                    />
                    <button
                      type="button"
                      className={`btn-checkbox ${correctAnswerData.correctAnswers.includes(index) ? 'checked' : ''}`}
                      onClick={() => toggleCorrectAnswerOption(index)}
                      title={correctAnswerData.correctAnswers.includes(index) ? 'Правильный ответ' : 'Отметить как правильный'}
                    >
                      {correctAnswerData.correctAnswers.includes(index) ? '✓' : '○'}
                    </button>
                    {correctAnswerData.answerOptions.length > 2 && (
                      <button
                        type="button"
                        className="btn-remove"
                        onClick={() => removeCorrectAnswerOption(index)}
                        title="Удалить вариант"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="btn-add-option"
                  onClick={addCorrectAnswerOption}
                >
                  + Добавить вариант ответа
                </button>
              </div>
            ) : isWhatIsOutputType() ? (
              <div className="form-group">
                <label htmlFor="correct_answer">Правильный ответ *</label>
                <input
                  type="text"
                  id="correct_answer"
                  value={formData.correct_answer}
                  onChange={(e) => setFormData({ ...formData, correct_answer: e.target.value })}
                  required
                  placeholder="Введите правильный ответ (например: 42, Hello, true и т.д.)"
                />
                <small style={{color: '#666', fontSize: '0.9em', display: 'block', marginTop: '5px'}}>
                  Введите правильный ответ в виде строки. Ответ будет сохранен как есть, без преобразования в JSON.
                </small>
              </div>
            ) : (
              <div className="form-group">
                <label htmlFor="correct_answer">Правильный ответ *</label>
                <textarea
                  id="correct_answer"
                  value={formData.correct_answer}
                  onChange={(e) => setFormData({ ...formData, correct_answer: e.target.value })}
                  required
                  rows={5}
                  placeholder='Введите правильный ответ. Можно ввести обычный текст или JSON (например: {"key": "value"}). Обычный текст будет автоматически обернут в объект {"text": "ваш текст"}.'
                />
                <small style={{color: '#666', fontSize: '0.9em', display: 'block', marginTop: '5px'}}>
                  Примеры: "Простой текст" или {"{"}"answer": "42", "explanation": "Потому что..."{"}"}
                </small>
              </div>
            )}
            
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingTask ? 'Сохранить' : 'Создать'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Тип</th>
              <th>Компания</th>
              <th>Технология</th>
              <th>Вопрос</th>
              <th>Описание</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {tasks.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-state">
                  Нет заданий. Добавьте первое задание.
                </td>
              </tr>
            ) : (
              tasks.map((task) => (
                <tr key={task.id}>
                  <td>{task.id}</td>
                  <td>{task.task_type?.name || '-'}</td>
                  <td>{task.company_name || '-'}</td>
                  <td>{task.topic_title || '-'}</td>
                  <td className="question-cell">{task.question_text.substring(0, 50)}...</td>
                  <td className="question-cell">
                    {task.description ? task.description.substring(0, 40) + '...' : '-'}
                  </td>
                  <td>
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(task)}
                      title="Редактировать"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(task.id)}
                      title="Удалить"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TestTasksSubTab;
